import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Tool, ToolInput } from "@portfolio/types";
import { apiGet, apiAdminPost, apiAdminPatch, apiAdminDelete } from "../../lib/api";
import { getAdminPassword, clearAdminPassword } from "../../lib/adminSession";

const emptyForm: ToolInput = {
  slug: "",
  title: "",
  description: "",
  icon: "",
  status: "soon",
  sort_order: 0,
};

export default function AdminTools() {
  const navigate = useNavigate();
  const password = getAdminPassword();

  const [tools, setTools] = useState<Tool[] | null>(null);
  const [form, setForm] = useState<ToolInput>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const data = await apiGet<Tool[]>("/api/v1/tools");
    setTools(data ?? []);
  }, []);

  useEffect(() => {
    if (!password) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
  }, [password, navigate, load]);

  if (!password) return null;

  function handleLogout() {
    clearAdminPassword();
    navigate("/admin", { replace: true });
  }

  // 401 nghĩa là session đang giữ mật khẩu sai/hết hiệu lực — buộc đăng nhập lại
  function handleUnauthorized() {
    clearAdminPassword();
    navigate("/admin", { replace: true });
  }

  function startEdit(tool: Tool) {
    setEditingId(tool.id);
    setForm({
      slug: tool.slug,
      title: tool.title,
      description: tool.description,
      icon: tool.icon ?? "",
      status: tool.status,
      sort_order: tool.sort_order,
    });
    setError(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = editingId
        ? await apiAdminPatch<null>(`/api/v1/tools/${editingId}`, password!, form)
        : await apiAdminPost<null>("/api/v1/tools", password!, form);

      if (!res.success) {
        if (res.error?.includes("mật khẩu")) return handleUnauthorized();
        setError(res.error ?? "Có lỗi xảy ra");
        return;
      }
      resetForm();
      await load();
    } catch {
      setError("Không kết nối được máy chủ");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Xoá công cụ này?")) return;
    const res = await apiAdminDelete<null>(`/api/v1/tools/${id}`, password!);
    if (!res.success) {
      if (res.error?.includes("mật khẩu")) return handleUnauthorized();
      setError(res.error ?? "Xoá thất bại");
      return;
    }
    if (editingId === id) resetForm();
    await load();
  }

  return (
    <div className="max-w-3xl mx-auto py-12 px-4 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Quản lý công cụ</h1>
        <button onClick={handleLogout} className="text-sm text-gray-400 hover:text-white">
          Đăng xuất
        </button>
      </div>

      <form onSubmit={handleSubmit} className="glass-card space-y-3">
        <h2 className="font-semibold">{editingId ? "Sửa công cụ" : "Thêm công cụ mới"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            required
            placeholder="Slug (vd: my-tool)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          />
          <input
            required
            placeholder="Tiêu đề"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          />
        </div>
        <textarea
          required
          placeholder="Mô tả"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          rows={2}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            placeholder="Icon (tên lucide, vd: Download)"
            value={form.icon ?? ""}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          />
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as ToolInput["status"] })}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          >
            <option value="soon">Sắp ra mắt</option>
            <option value="live">Đã hoạt động</option>
          </select>
          <input
            type="number"
            placeholder="Thứ tự"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
          />
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving} className="btn-gradient text-sm disabled:opacity-50">
            {saving ? "Đang lưu..." : editingId ? "Cập nhật" : "Thêm mới"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-sm text-gray-400 hover:text-white px-4">
              Huỷ
            </button>
          )}
        </div>
      </form>

      <div className="space-y-2">
        {tools === null && <p className="text-gray-500 text-sm">Đang tải...</p>}
        {tools?.length === 0 && <p className="text-gray-500 text-sm">Chưa có công cụ nào.</p>}
        {tools?.map((tool) => (
          <div
            key={tool.id}
            className="flex items-center justify-between gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <b className="truncate">{tool.title}</b>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    tool.status === "live" ? "bg-green-500/20 text-green-400" : "bg-white/10 text-gray-400"
                  }`}
                >
                  {tool.status === "live" ? "Hoạt động" : "Sắp ra mắt"}
                </span>
              </div>
              <p className="text-sm text-gray-500 truncate">/{tool.slug}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(tool)} className="text-sm text-gray-400 hover:text-white px-2">
                Sửa
              </button>
              <button onClick={() => handleDelete(tool.id)} className="text-sm text-red-400 hover:text-red-300 px-2">
                Xoá
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
